import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import SeoHead from '@/components/layout/SeoHead';

const PIN = 'ohana2026';
const STORAGE_KEY_AUTH = 'ohana_sop_auth_v1';
const STORAGE_KEY_CHECKLIST = 'ohana_sop_checklist_v1';
const LAST_REVIEWED = 'May 8, 2026';
const LEGAL_ENTITY = 'Ohana Property & Transition Services LLC';

const SECTIONS = [
  { id: 'sec-1', number: '1', title: 'Core Disposal Policy' },
  { id: 'sec-2', number: '2', title: 'Pre-Job Disposal Screening' },
  { id: 'sec-3', number: '3', title: 'Required Sorting Categories' },
  { id: 'sec-4', number: '4', title: 'On-Site Field Procedure' },
  { id: 'sec-4a', number: '4a', title: 'Road Transport Compliance' },
  { id: 'sec-5', number: '5', title: 'Stop Work Procedure', critical: true },
  { id: 'sec-6', number: '6', title: 'Facility Rejection Procedure' },
  { id: 'sec-7', number: '7', title: 'Donation and Reuse Procedure' },
  { id: 'sec-8', number: '8', title: 'Special Item Handling' },
  { id: 'sec-9', number: '9', title: 'Pricing Controls' },
  { id: 'sec-10', number: '10', title: 'Documentation Requirements' },
  { id: 'sec-11', number: '11', title: 'Internal Compliance Rule' },
  { id: 'sec-12', number: '12', title: 'Subcontractor and Vendor Requirements' },
  { id: 'sec-13', number: '13', title: 'Customer Communication Scripts' },
  { id: 'sec-14', number: '14', title: 'Job Closeout' },
  { id: 'sec-15', number: '15', title: 'Quick Field Checklist' },
  { id: 'sec-16', number: '16', title: 'Compliance References' },
  { id: 'sec-17', number: '17', title: 'SOP Summary' },
];

const SECTION_15_CHECKLIST = [
  {
    group: 'Before Loading',
    items: [
      'Scope confirmed with customer',
      'Customer authority confirmed',
      'Before photos taken',
      'Donation permission confirmed in writing',
      'Full hazard scan completed',
      'Special-fee items identified and priced',
      'Excluded materials identified',
      'Price confirmed or updated based on walkthrough',
    ],
  },
  {
    group: 'During Loading',
    items: [
      'Donation items separated and kept clean',
      'Recycling separated from landfill items',
      'Standard landfill items loaded',
      'Special-fee items separated',
      'No prohibited items mixed into landfill load',
    ],
  },
  {
    group: 'Before Leaving Job Site',
    items: [
      'Load tarped or fully secured',
      'No loose items visible or accessible from outside vehicle',
      'Trailer properly connected and safety chains attached',
      'Lights and signals functional',
      'Load weight within vehicle capacity',
      'Route to disposal facility confirmed',
    ],
  },
  {
    group: 'After Disposal',
    items: [
      'Receipts saved and filed',
      'Rejected items documented if applicable',
      'After photos taken',
      'Customer updated on job completion',
      'Payment collected',
      'Job record completed in CRM',
    ],
  },
];

/* -------------------- PIN GATE -------------------- */

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pin.trim() === PIN) {
      try {
        sessionStorage.setItem(STORAGE_KEY_AUTH, '1');
      } catch (err) {
        // Non-critical: localStorage / sessionStorage / clipboard can fail in private
        // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[sop] non-critical storage/clipboard op failed:', err);
        }
      }
      onUnlock();
    } else {
      setError('Incorrect PIN. Try again.');
      setPin('');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-ohana-bg">
      <header className="bg-ohana-primary text-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-white/10 grid place-items-center text-white font-serif text-base">
            O
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/70">Internal</p>
            <h1 className="text-base sm:text-lg font-serif leading-tight">
              Field Operations SOP
            </h1>
          </div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-5 py-12">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm bg-white border border-ohana-border rounded-2xl p-6 shadow-sm"
          data-testid="sop-pin-form"
        >
          <h2 className="font-serif text-2xl text-ohana-primary leading-tight">
            Authorized crew only
          </h2>
          <p className="mt-2 text-sm text-ohana-text-muted">
            Enter the operations PIN to access the field SOP.
          </p>

          <label
            htmlFor="sop-pin-input"
            className="mt-5 block text-xs uppercase tracking-wider text-ohana-text-muted"
          >
            Operations PIN
          </label>
          <input
            id="sop-pin-input"
            type="password"
            inputMode="text"
            autoComplete="off"
            autoFocus
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              if (error) setError('');
            }}
            className="mt-1 w-full rounded-lg border border-ohana-border bg-ohana-bg px-3 py-3 text-base text-ohana-text focus:outline-none focus:ring-2 focus:ring-ohana-primary/40 focus:border-ohana-primary"
            data-testid="sop-pin-input"
          />

          {error && (
            <p
              className="mt-2 text-sm text-red-700"
              role="alert"
              data-testid="sop-pin-error"
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            className="mt-5 w-full rounded-lg bg-ohana-primary text-white font-medium py-3 hover:bg-ohana-primary-dark transition-colors"
            data-testid="sop-pin-submit"
          >
            Unlock SOP
          </button>

          <p className="mt-4 text-[11px] text-ohana-text-muted leading-relaxed">
            This page is for authorized Ohana personnel and approved subcontractors. Do not share this PIN.
          </p>
        </form>
      </main>
    </div>
  );
}

/* -------------------- LAYOUT HELPERS -------------------- */

const P = ({ children }) => (
  <p className="text-[15px] leading-relaxed text-ohana-text">{children}</p>
);

const UL = ({ children }) => (
  <ul className="list-disc pl-5 space-y-1.5 text-[15px] leading-relaxed text-ohana-text marker:text-ohana-primary/60">
    {children}
  </ul>
);

const OL = ({ children }) => (
  <ol className="list-decimal pl-5 space-y-1.5 text-[15px] leading-relaxed text-ohana-text marker:text-ohana-primary/60">
    {children}
  </ol>
);

const SubH = ({ children }) => (
  <h3 className="font-serif text-lg text-ohana-primary mt-5 first:mt-0">{children}</h3>
);

const Quote = ({ children }) => (
  <blockquote className="border-l-3 border-ohana-accent bg-ohana-surface/70 rounded-r-md px-4 py-3 text-[15px] leading-relaxed italic text-ohana-text">
    {children}
  </blockquote>
);

/* -------------------- SECTION CONTENT -------------------- */

function SectionContent({ id }) {
  switch (id) {
    case 'sec-1':
      return (
        <div className="space-y-4">
          <P>Ohana handles standard nonhazardous cleanout materials only unless a qualified third-party provider is retained.</P>
          <P>Ohana does not handle, transport, remediate, or dispose of hazardous, infectious, medical, asbestos-containing, lead-contaminated, mold-remediation, chemical, explosive, or otherwise regulated materials.</P>
          <P>All disposal is subject to local facility rules including the Bartholomew County Landfill, the Columbus / Bartholomew Recycling Center, donation partners, recycling vendors, and any licensed third-party disposal provider.</P>
          <P>The Bartholomew County Landfill accepts waste from Bartholomew County residents and businesses but bans items such as liquids, PCBs, oil, oil-based paints, hazardous waste, yard waste, tree trimmings, white goods and appliances, closed-top drums, hot burn barrels, electronic waste, batteries, solar panels, and whole tires from normal landfill disposal. Loose loads must be tarped or a surcharge may apply.</P>
        </div>
      );

    case 'sec-2':
      return (
        <div className="space-y-4">
          <P>Before giving a final quote, ask the customer the following questions.</P>
          <UL>
            <li><strong>What type of property is this?</strong> House, apartment, rental, garage, storage unit, estate, foreclosure, move-out, commercial, or construction-related.</li>
            <li><strong>What items are included?</strong> Furniture, mattresses, bagged trash, appliances, electronics, tires, paint, chemicals, fuel, batteries, carpet, construction debris, yard waste, etc.</li>
            <li><strong>Are there any hazardous or questionable items?</strong> Needles, chemicals, oil, fuel, propane tanks, paint, pesticides, medical waste, mold, asbestos, animal waste, dead animals, ammunition, or unknown liquids.</li>
            <li><strong>Can they send photos?</strong> Photos are required when possible for pricing, sorting, and risk assessment.</li>
            <li><strong>Do they want reusable items donated or redirected?</strong> Get written permission before donation or reuse.</li>
          </UL>
        </div>
      );

    case 'sec-3':
      return (
        <div className="space-y-5">
          <P>Every job should be sorted into the following categories before or during loading.</P>

          <SubH>Category A. Donation / Reuse</SubH>
          <P>Includes clean, usable household goods such as:</P>
          <UL>
            <li>Furniture in usable condition</li>
            <li>Housewares</li>
            <li>Small appliances that work</li>
            <li>Clothing, linens, and household items if acceptable to partner</li>
            <li>Tools</li>
            <li>Decor</li>
            <li>Baby items only if accepted by partner and confirmed safe</li>
            <li>Disaster-relief or transition-support items</li>
          </UL>
          <P><strong>Requirement:</strong> Customer must authorize donation or reuse in writing before any items are redirected.</P>
          <P><strong>Rule:</strong> Do not promise tax receipts or assign donation value to items.</P>

          <SubH>Category B. Recycling</SubH>
          <P>Includes items accepted by recycling facilities or vendors:</P>
          <UL>
            <li>Cardboard</li>
            <li>Paper</li>
            <li>Accepted plastics</li>
            <li>Metals and glass</li>
            <li>Scrap metal</li>
            <li>Certain electronics if accepted by proper facility</li>
            <li>Appliances only through accepted appliance and refrigerant process</li>
          </UL>
          <P>The Columbus / Bartholomew Recycling Center uses a two-sort recycling process where sorted cardboard is separate from commingled paper, plastics, metals, and glass. It accepts residential e-waste but not business e-waste. TVs and computer monitors have a listed fee.</P>

          <SubH>Category C. Standard Landfill Disposal</SubH>
          <P>Includes standard nonhazardous junk such as:</P>
          <UL>
            <li>Broken furniture</li>
            <li>Bagged household trash</li>
            <li>Non-reusable items</li>
            <li>General household junk</li>
            <li>Non-regulated cleanout debris</li>
            <li>Non-recyclable material accepted by landfill</li>
          </UL>
          <P><strong>Rule:</strong> Loose loads must be tarped. Untarped loose loads can trigger a surcharge at the Bartholomew County Landfill.</P>

          <SubH>Category D. Special Fee / Special Handling Items</SubH>
          <P>These may be accepted only under special rules or fees:</P>
          <UL>
            <li>TVs and computer monitors</li>
            <li>Refrigerators, freezers, air conditioners, and dehumidifiers</li>
            <li>CFC appliances</li>
            <li>Tires</li>
            <li>Electronics</li>
            <li>Large or heavy items</li>
            <li>Carpet and construction debris depending on facility rules</li>
          </UL>
          <P>The Bartholomew County Landfill lists fees for TV and computer monitors, CFC appliances, tires, and loads over 200 lbs. Trailers over 4 feet are not allowed on the small-load pad.</P>

          <SubH>Category E. Excluded / Third-Party Only</SubH>
          <P>Ohana does not handle these materials directly:</P>
          <UL>
            <li>Hazardous waste</li>
            <li>Infectious or medical waste</li>
            <li>Needles or sharps</li>
            <li>Chemicals, fuel, motor oil, and pesticides</li>
            <li>Solvents and unknown liquids</li>
            <li>Wet paint and oil-based paint</li>
            <li>Lead acid batteries</li>
            <li>Propane tanks and pressurized cylinders</li>
            <li>Asbestos-containing materials</li>
            <li>Lead-contaminated materials</li>
            <li>Mold remediation materials</li>
            <li>Human or animal waste and biohazards</li>
            <li>Dead animals</li>
            <li>Ammunition, firearms, and explosives</li>
            <li>Flammable materials</li>
            <li>Industrial waste</li>
            <li>Any regulated material requiring licensing, manifesting, or special disposal</li>
          </UL>
          <P>The City of Columbus lists prohibited wastes for city sanitation collection including lead acid batteries, propane tanks, move-out or clean-out trash over 20 bags or boxes, motor oil, tires, wet paint, hazardous or infectious waste, construction debris, concrete, roofing, and carpeting.</P>
        </div>
      );

    case 'sec-4':
      return (
        <div className="space-y-5">
          <SubH>Step 1. Arrival and Walkthrough</SubH>
          <P>Upon arrival at the job site:</P>
          <OL>
            <li>Confirm customer name and service address</li>
            <li>Confirm scope of work</li>
            <li>Confirm items to remove</li>
            <li>Confirm items to leave</li>
            <li>Identify donation and reuse items</li>
            <li>Identify special-fee items</li>
            <li>Look for excluded or hazardous materials</li>
            <li>Take before photos</li>
            <li>Confirm access, parking, and load path</li>
          </OL>
          <P><strong>Do not start loading until scope is clear.</strong></P>

          <SubH>Step 2. Site Risk Check</SubH>
          <P>Look for the following before loading begins:</P>
          <UL>
            <li>Needles or sharps</li>
            <li>Chemicals or fuel</li>
            <li>Pesticides or unknown liquids</li>
            <li>Mold or water damage</li>
            <li>Suspected asbestos</li>
            <li>Suspected lead paint debris</li>
            <li>Animal or human waste</li>
            <li>Pest infestation</li>
            <li>Weak flooring or unsafe stairs</li>
            <li>Aggressive animals</li>
            <li>Structural instability</li>
            <li>Conflicting customer instructions</li>
          </UL>
          <P>If any of the above are discovered, follow the Stop Work Procedure in Section 5.</P>

          <SubH>Step 3. Sort Before Loading</SubH>
          <P>Sort all items into the following categories before loading:</P>
          <OL>
            <li>Donation and reuse</li>
            <li>Recycling</li>
            <li>Scrap metal</li>
            <li>Standard landfill</li>
            <li>Special-fee items</li>
            <li>Excluded and third-party only</li>
          </OL>
          <P>Do not mix excluded materials into landfill loads. Do not hide prohibited items under other debris. Do not attempt to bring prohibited items into any facility.</P>

          <SubH>Step 4. Load Securement</SubH>
          <P>Before leaving the job site:</P>
          <UL>
            <li>Tarp all loose loads</li>
            <li>Strap large items securely</li>
            <li>Keep weight balanced</li>
            <li>Do not overload the truck or trailer</li>
            <li>Keep sharp items contained</li>
            <li>Keep donation items clean and separated from landfill items</li>
            <li>Collect and retain receipts and facility documentation</li>
            <li>Photograph load if needed for documentation</li>
          </UL>
          <P>Loose loads at the Bartholomew County Landfill must be tarped or a surcharge will apply.</P>
        </div>
      );

    case 'sec-4a':
      return (
        <div className="space-y-5">
          <P>All Ohana vehicles and trailers operating on Indiana public roads must comply with Indiana Department of Transportation (INDOT) and Indiana Bureau of Motor Vehicles (BMV) requirements at all times.</P>

          <SubH>Load Securement</SubH>
          <P>All loads must be covered with a tarp or fully secured before leaving any job site. Loose, unsecured, or uncovered loads on Indiana roads are a violation of Indiana Code 9-21-8-40 and may result in fines, liability for road damage, and liability for injuries or property damage caused by debris falling from the vehicle.</P>
          <P><strong>Do not leave a job site with an unsecured or uncovered load under any circumstances.</strong></P>

          <SubH>Weight Limits</SubH>
          <P>Drivers must be aware of the gross vehicle weight rating (GVWR) of their truck or trailer combination. Overloaded vehicles are subject to fines at Indiana weigh stations and inspection points. If a load exceeds the vehicle&apos;s rated capacity, split the load into multiple trips rather than overloading.</P>

          <SubH>Commercial Vehicle Requirements</SubH>
          <P>Any vehicle with a GVWR exceeding 26,000 pounds requires a valid Commercial Driver&apos;s License (CDL). Drivers operating vehicles at or above this threshold without a CDL are in violation of Indiana law.</P>

          <SubH>Trailers</SubH>
          <P>The Bartholomew County Landfill does not allow trailers over 4 feet on the small-load pad. Plan trailer use accordingly before arriving at the facility.</P>

          <SubH>Vehicle Condition</SubH>
          <P>All Ohana vehicles must be maintained in safe operating condition including functional lights, brakes, tires, and tarp or tie-down systems before each job.</P>

          <SubH>Insurance</SubH>
          <P>All Ohana vehicles used for commercial hauling must carry appropriate commercial auto insurance. Personal auto insurance policies typically do not cover commercial hauling activity.</P>

          <SubH>Driver Responsibility</SubH>
          <P>Every driver is personally responsible for verifying their load is properly secured before leaving any job site.</P>
          <P>Before leaving any job site, verify all of the following:</P>
          <UL>
            <li>Load is tarped or fully secured</li>
            <li>No loose items visible or accessible from outside the vehicle</li>
            <li>Trailer is properly connected and safety chains are attached</li>
            <li>Lights and signals are functional</li>
            <li>Load weight is within vehicle capacity</li>
            <li>Route to disposal facility is confirmed</li>
          </UL>

          <SubH>References</SubH>
          <UL>
            <li>INDOT: <a className="text-ohana-primary underline underline-offset-2" href="https://www.in.gov/indot" target="_blank" rel="noopener noreferrer">in.gov/indot</a></li>
            <li>Indiana Code 9-21-8-40. Throwing or depositing materials on highway prohibited</li>
            <li>Indiana BMV Commercial Driver Licensing: <a className="text-ohana-primary underline underline-offset-2" href="https://www.in.gov/bmv/commercial-driver-licensing" target="_blank" rel="noopener noreferrer">in.gov/bmv/commercial-driver-licensing</a></li>
          </UL>
        </div>
      );

    case 'sec-5':
      return (
        <div className="space-y-5">
          <div
            className="rounded-lg bg-red-700 text-white px-4 py-3.5 shadow-md"
            role="alert"
            data-testid="sop-stop-work-banner"
          >
            <p className="font-serif text-base sm:text-lg leading-tight">
              STOP WORK. Do not proceed. Contact operations lead immediately.
            </p>
          </div>

          <P>Stop work immediately if any of the following conditions are present:</P>
          <UL>
            <li>Hazardous or unknown material is found</li>
            <li>Needles, sharps, or medical waste are found</li>
            <li>Chemicals, fuel, oil, or pesticides are found</li>
            <li>Suspected asbestos, lead, mold, or biohazard is present</li>
            <li>Customer ownership or authority is unclear</li>
            <li>Items are disputed by any party</li>
            <li>Facility rules prevent proper disposal</li>
            <li>Structural or safety hazards exist for the crew</li>
            <li>Crew lacks required PPE, licensing, or equipment</li>
          </UL>

          <SubH>Stop Work Steps</SubH>
          <OL>
            <li>Stop loading the affected area immediately</li>
            <li>Do not touch or move suspect material</li>
            <li>Photograph only if safe to do so</li>
            <li>Notify the customer</li>
            <li>Notify the Ohana operations lead</li>
            <li>Document the issue in the job record</li>
            <li>Require third-party certified provider if needed</li>
            <li>Reprice, reschedule, or cancel affected scope as necessary</li>
          </OL>

          <SubH>Script for Customer Communication</SubH>
          <P>Use this language when stopping work:</P>
          <Quote>
            &ldquo;For safety and compliance, we cannot remove this material directly. It may require a properly licensed third-party provider. We can continue with the nonhazardous items if the area is safe, or pause the job until this is handled.&rdquo;
          </Quote>
        </div>
      );

    case 'sec-6':
      return (
        <div className="space-y-4">
          <P>If a landfill, recycling center, donation partner, or disposal vendor rejects an item:</P>
          <OL>
            <li>Do not argue with facility staff</li>
            <li>Ask why the item was rejected</li>
            <li>Document the reason in the job record</li>
            <li>Take a photo if allowed</li>
            <li>Keep any rejection notice or receipt</li>
            <li>Contact the Ohana operations lead</li>
            <li>Contact the customer if extra handling or fees are needed</li>
            <li>Do not dump illegally under any circumstances</li>
            <li>Route the item to the proper facility or third-party provider</li>
          </OL>
          <P>Customer remains responsible for added disposal, handling, special processing, return-trip, or third-party costs if the item was undisclosed or rejected due to facility rules.</P>
        </div>
      );

    case 'sec-7':
      return (
        <div className="space-y-5">
          <P>Donation and reuse is only allowed with written customer permission.</P>

          <SubH>Donation Flow</SubH>
          <OL>
            <li>Identify usable items during the job walkthrough</li>
            <li>Confirm customer authorization in writing</li>
            <li>Photograph items before removal</li>
            <li>Keep items separate from landfill items during loading</li>
            <li>Transport to approved donation partner, storage, or community redistribution channel</li>
            <li>Document the receiving partner or destination</li>
            <li>Do not promise item value to the customer</li>
            <li>Do not issue tax receipts unless a qualified nonprofit partner independently provides one</li>
          </OL>

          <SubH>Customer Authorization Checkboxes</SubH>
          <P>Use on all intake forms or service agreements:</P>
          <UL>
            <li>Customer authorizes donation, reuse, or community redistribution of usable items</li>
            <li>Customer does not authorize donation or reuse</li>
            <li>Customer wants these specific items excluded from removal: ___________</li>
            <li>Customer gives Ohana permission to use before and after photos for portfolio and marketing purposes (optional)</li>
          </UL>
        </div>
      );

    case 'sec-8':
      return (
        <div className="space-y-5">
          <SubH>Electronics, TVs, and Monitors</SubH>
          <UL>
            <li>Confirm facility acceptance before loading</li>
            <li>Quote extra fees upfront to the customer</li>
            <li>Keep separate from the general landfill load</li>
            <li>Collect fee documentation when possible</li>
          </UL>

          <SubH>Appliances and CFC Items</SubH>
          <UL>
            <li>Refrigerators, freezers, AC units, and dehumidifiers may require special handling</li>
            <li>Do not damage refrigerant lines</li>
            <li>Confirm fee and facility rule before accepting these items</li>
          </UL>

          <SubH>Tires</SubH>
          <UL>
            <li>Quote tires separately from the general load</li>
            <li>Confirm whether tires are whole or quartered before quoting</li>
            <li>Do not mix tires into the general landfill load</li>
          </UL>

          <SubH>Paint</SubH>
          <UL>
            <li>Water-based paint cans must have lids off and paint dried out before standard disposal per Columbus city guidelines</li>
            <li>Wet paint and oil-based paint are not standard disposal items</li>
            <li>Treat unknown paint as excluded until verified</li>
          </UL>

          <SubH>Propane Tanks and Pressurized Cylinders</SubH>
          <UL>
            <li>Do not load propane tanks or pressurized cylinders as standard junk</li>
            <li>Refer to approved exchange, recycling, or hazardous specialty disposal provider</li>
          </UL>

          <SubH>Batteries</SubH>
          <UL>
            <li>Do not place lead acid batteries in standard trash</li>
            <li>Route through approved recycling or disposal facility</li>
          </UL>
        </div>
      );

    case 'sec-9':
      return (
        <div className="space-y-5">
          <P>Every quote must account for all of the following as applicable:</P>
          <UL>
            <li>Labor time and crew size</li>
            <li>Truck or trailer load volume</li>
            <li>Weight of items</li>
            <li>Disposal fees at receiving facility</li>
            <li>Fuel and distance</li>
            <li>Stairs or elevator access</li>
            <li>Heavy or oversized items</li>
            <li>Long carry distance from property to vehicle</li>
            <li>Sorting time for donation and recycling</li>
            <li>Special item fees</li>
            <li>Facility fees and surcharges</li>
            <li>Return trips if needed</li>
            <li>Third-party provider costs</li>
            <li>Risk premium for uncertain or complex jobs</li>
          </UL>

          <SubH>Required Estimate Disclaimer</SubH>
          <P>Use this note on every estimate provided to customers:</P>
          <Quote>
            &ldquo;Final pricing may change based on actual volume, weight, labor, access, stairs, disposal facility fees, recycling fees, special item fees, rejected materials, hazardous discovery, or third-party disposal requirements.&rdquo;
          </Quote>
        </div>
      );

    case 'sec-10':
      return (
        <div className="space-y-5">
          <SubH>Standard Job Documentation</SubH>
          <P>Every job must include:</P>
          <UL>
            <li>Customer name and contact information</li>
            <li>Service address</li>
            <li>Date and time of service</li>
            <li>Before photos</li>
            <li>After photos</li>
            <li>Load photos when helpful</li>
            <li>Donation authorization form or checkbox record</li>
            <li>Disposal receipts</li>
            <li>Recycling receipts</li>
            <li>Special item fees and documentation</li>
            <li>Facility rejection notes if applicable</li>
            <li>Stop work notes if applicable</li>
            <li>Payment record</li>
            <li>Customer approval or sign-off</li>
          </UL>

          <SubH>Additional Documentation for Higher-Risk Jobs</SubH>
          <P>For complex or higher-risk jobs also document:</P>
          <UL>
            <li>Known hazards identified before or during the job</li>
            <li>Excluded materials and how they were handled</li>
            <li>Confirmation of customer authority</li>
            <li>Third-party referral details</li>
            <li>Items left behind due to facility rules</li>
            <li>Change order or reprice approval from customer</li>
          </UL>
        </div>
      );

    case 'sec-11':
      return (
        <div className="space-y-4">
          <P>No crew member, helper, subcontractor, or vendor may dispose of materials in a way that violates:</P>
          <UL>
            <li>Customer agreement</li>
            <li>This Ohana Disposal SOP</li>
            <li>Bartholomew County Landfill rules</li>
            <li>Columbus / Bartholomew Recycling Center rules</li>
            <li>Donation partner acceptance policies</li>
            <li>Indiana solid waste regulations</li>
            <li>IDEM requirements</li>
            <li>INDOT load securement and vehicle regulations</li>
            <li>Local ordinances in any service area</li>
            <li>OSHA general industry safety standards</li>
            <li>Any applicable license or permit requirement</li>
          </UL>
          <P>Illegal dumping, unauthorized disposal, hiding prohibited materials, or accepting side deals are grounds for immediate termination of the contractor or vendor relationship.</P>
        </div>
      );

    case 'sec-12':
      return (
        <div className="space-y-4">
          <P>Any contractor or vendor assisting Ohana must follow this SOP before beginning any job.</P>
          <P>Before using a contractor or vendor, collect the following as applicable:</P>
          <UL>
            <li>W-9</li>
            <li>Driver&apos;s license</li>
            <li>Proof of liability insurance</li>
            <li>Commercial auto insurance certificate</li>
            <li>Workers compensation documentation if applicable</li>
            <li>Business registration if applicable</li>
            <li>Specialty certification if handling regulated work</li>
            <li>Signed subcontractor or vendor agreement</li>
            <li>Signed job-specific work order</li>
          </UL>
          <P>Contractors may not handle excluded or regulated materials unless they are properly licensed, insured, certified, and approved in writing by Ohana management.</P>
        </div>
      );

    case 'sec-13':
      return (
        <div className="space-y-5">
          <SubH>Normal Quote</SubH>
          <Quote>
            &ldquo;Based on the photos and description, we can give you a working estimate. Final pricing depends on actual volume, weight, access, disposal fees, and whether any special-handling items are found on site.&rdquo;
          </Quote>

          <SubH>Donation Authorization</SubH>
          <Quote>
            &ldquo;If there are usable items, we can separate them for donation or reuse with your permission. We do not assign tax value, but we can document what was redirected when possible.&rdquo;
          </Quote>

          <SubH>Hazardous Item Discovery</SubH>
          <Quote>
            &ldquo;We are going to pause this section. That item may require a certified third-party provider, and we do not want to risk unsafe or improper disposal.&rdquo;
          </Quote>

          <SubH>Facility Rejection</SubH>
          <Quote>
            &ldquo;The facility did not accept this item under their current rules. We will need to either route it to a proper specialty provider or update the quote for additional handling.&rdquo;
          </Quote>

          <SubH>Customer Asking &ldquo;Can You Just Take It Anyway?&rdquo;</SubH>
          <Quote>
            &ldquo;I cannot put Ohana or you at risk by disposing of something improperly. We can handle the approved nonhazardous items and help identify the proper next step for the restricted material.&rdquo;
          </Quote>

          <SubH>Multi-Trip Jobs</SubH>
          <Quote>
            &ldquo;We come prepared based on your photos so most jobs get done efficiently in one visit. For larger estates or heavy volume cleanouts we will tell you exactly what to expect before we schedule so there are no surprises and no delays.&rdquo;
          </Quote>
        </div>
      );

    case 'sec-14':
      return (
        <div className="space-y-4">
          <P>Before closing any job:</P>
          <OL>
            <li>Confirm all approved items were removed</li>
            <li>Confirm excluded items left behind are documented with reason</li>
            <li>Confirm donation and reuse items were separated and documented</li>
            <li>Confirm all disposal and recycling receipts are saved</li>
            <li>Take after photos of the cleared property</li>
            <li>Collect payment</li>
            <li>Request a review if the customer is satisfied</li>
            <li>Log any lessons learned for SOP improvement</li>
          </OL>
        </div>
      );

    case 'sec-15':
      return <Section15Checklist />;

    case 'sec-16':
      return <Section16References />;

    case 'sec-17':
      return (
        <div className="space-y-4">
          <P>Ohana&apos;s disposal system follows this sequence on every job:</P>
          <p className="text-[15px] leading-relaxed text-ohana-primary font-medium">
            Inspect → Sort → Authorize → Load Safely → Secure for Road → Dispose Legally → Document → Close Out
          </p>

          <SubH>Operating Rule</SubH>
          <P>When in doubt, stop work, document, and escalate.</P>

          <P>The goal is not just to remove junk fast. The goal is to protect the customer, protect Ohana, comply with all local and state disposal rules, preserve reusable items when authorized, support The Hearth Project mission, and avoid stepping into regulated waste without a qualified third-party provider.</P>

          <P className="text-ohana-text-muted">
            Ohana Property Services is a trade name of Ohana Property &amp; Transition Services LLC, a registered Indiana limited liability company. This SOP is an internal operational document for authorized Ohana personnel and approved subcontractors only.
          </P>
          <p className="text-xs text-ohana-text-muted">Last updated: May 8, 2026</p>
        </div>
      );

    default:
      return null;
  }
}

/* -------------------- INTERACTIVE SECTION 16 REFERENCES -------------------- */

const COMPLIANCE_REFERENCES = [
  ['Bartholomew County Solid Waste', 'bcswmd.com', '(812) 376-2614', '+18123762614'],
  ['Columbus Dept. of Public Works', 'columbus.in.gov/public-works', '(812) 376-2509', '+18123762509'],
  ['Indiana IDEM', 'in.gov/idem', 'n/a', null],
  ['Indiana INDOT', 'in.gov/indot', 'n/a', null],
  ['Indiana BMV Commercial', 'in.gov/bmv/commercial-driver-licensing', 'n/a', null],
  ['Federal EPA Solid Waste', 'epa.gov/smm', 'n/a', null],
  ['Indiana Code 9-21-8-40', 'iga.in.gov', 'n/a', null],
  ['OSHA General Industry', 'osha.gov', '1-800-321-6742', '+18003216742'],
];

const DISPOSAL_FACILITIES = [
  {
    name: 'Bartholomew County Landfill',
    address: '811 E CR 450 S, Columbus, IN 47201',
    mapsUrl: 'https://maps.google.com/?q=811+E+CR+450+S+Columbus+IN+47201',
  },
  {
    name: 'Columbus Bartholomew Recycling Center',
    address: '720 S Mapleton St, Columbus, IN 47201',
    mapsUrl: 'https://maps.google.com/?q=720+S+Mapleton+St+Columbus+IN+47201',
  },
  {
    name: 'Columbus Dept. of Public Works',
    address: '2250 Kreutzer Dr, Columbus, IN 47201',
    mapsUrl: 'https://maps.google.com/?q=2250+Kreutzer+Dr+Columbus+IN+47201',
  },
];

const EMERGENCY_CONTACTS_TEXT =
  'BCSWMD: (812) 376-2614\nColumbus Public Works: (812) 376-2509\nOSHA: 1-800-321-6742';

function Section16References() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(EMERGENCY_CONTACTS_TEXT);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = EMERGENCY_CONTACTS_TEXT;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sop] copy to clipboard failed:', err);
      }
      setCopied(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <button
          type="button"
          onClick={handleCopy}
          className="w-full flex items-center justify-center gap-2.5 rounded-xl bg-ohana-primary text-white font-medium py-4 px-5 text-base hover:bg-ohana-primary-dark active:bg-ohana-primary-dark transition-colors shadow-sm"
          data-testid="sop-copy-emergency-contacts"
          aria-live="polite"
        >
          {copied ? (
            <>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12l6.8-6.7a1 1 0 011.4 0z" clipRule="evenodd" />
              </svg>
              <span>Copied to clipboard</span>
            </>
          ) : (
            <>
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M7 3a2 2 0 00-2 2v1H4a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-1h1a2 2 0 002-2V5a2 2 0 00-2-2H7zm0 2h8v8h-1V8a2 2 0 00-2-2H7V5zM4 8h8v8H4V8z" />
              </svg>
              <span>Copy Emergency Contacts</span>
            </>
          )}
        </button>
        <p className="mt-2 text-xs text-ohana-text-muted text-center">
          BCSWMD · Columbus Public Works · OSHA
        </p>
      </div>

      {/* Disposal facilities. Tap-to-navigate */}
      <div data-testid="sop-disposal-facilities">
        <h3 className="font-serif text-lg text-ohana-primary mb-2">Disposal Facilities</h3>
        <ul className="space-y-2.5">
          {DISPOSAL_FACILITIES.map((f) => (
            <li
              key={f.name}
              className="bg-white border border-ohana-border rounded-xl p-3.5"
            >
              <p className="font-medium text-ohana-text leading-snug">{f.name}</p>
              <p className="mt-0.5 text-sm text-ohana-text-muted leading-snug">
                {f.address}
              </p>
              <a
                href={f.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-lg bg-ohana-surface hover:bg-ohana-border/70 active:bg-ohana-border text-ohana-primary font-medium px-4 py-2.5 text-sm border border-ohana-border transition-colors min-h-[44px]"
                data-testid={`sop-maps-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 2a6 6 0 00-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 00-6-6zm0 8.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" clipRule="evenodd" />
                </svg>
                <span>Open in Maps</span>
                <svg className="h-3.5 w-3.5 text-ohana-text-muted" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M11 3a1 1 0 100 2h2.586l-5.293 5.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                  <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="overflow-x-auto -mx-1 sm:mx-0">
        <table className="w-full text-sm border border-ohana-border rounded-lg overflow-hidden">
          <thead className="bg-ohana-primary text-white">
            <tr>
              <th className="text-left font-medium px-3 py-2.5">Authority</th>
              <th className="text-left font-medium px-3 py-2.5">Resource</th>
              <th className="text-left font-medium px-3 py-2.5">Contact</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {COMPLIANCE_REFERENCES.map(([authority, resource, contact, telHref]) => (
              <tr key={authority} className="border-t border-ohana-border">
                <td className="px-3 py-2.5 align-top text-ohana-text">{authority}</td>
                <td className="px-3 py-2.5 align-top text-ohana-text break-all">{resource}</td>
                <td className="px-3 py-2.5 align-top text-ohana-text whitespace-nowrap">
                  {telHref ? (
                    <a
                      href={`tel:${telHref}`}
                      className="text-ohana-primary font-medium underline underline-offset-2 hover:text-ohana-primary-dark"
                      data-testid={`sop-tel-${telHref}`}
                    >
                      {contact}
                    </a>
                  ) : (
                    contact
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- INTERACTIVE SECTION 15 CHECKLIST -------------------- */

function Section15Checklist() {
  const [checked, setChecked] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_CHECKLIST);
      if (raw) setChecked(JSON.parse(raw));
    } catch (err) {
      // Non-critical: localStorage / sessionStorage / clipboard can fail in private
      // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sop] non-critical storage/clipboard op failed:', err);
      }
    }
  }, []);

  const persist = useCallback((next) => {
    setChecked(next);
    try {
      localStorage.setItem(STORAGE_KEY_CHECKLIST, JSON.stringify(next));
    } catch (err) {
      // Non-critical: localStorage / sessionStorage / clipboard can fail in private
      // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sop] non-critical storage/clipboard op failed:', err);
      }
    }
  }, []);

  const toggle = (key) => {
    persist({ ...checked, [key]: !checked[key] });
  };

  const resetAll = () => {
    persist({});
  };

  const totalCount = useMemo(
    () => SECTION_15_CHECKLIST.reduce((acc, g) => acc + g.items.length, 0),
    []
  );
  const checkedCount = useMemo(
    () => Object.values(checked).filter(Boolean).length,
    [checked]
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-ohana-text-muted" data-testid="sop-checklist-progress">
          <span className="font-medium text-ohana-text">{checkedCount}</span>
          <span> / {totalCount} complete</span>
        </p>
        <button
          type="button"
          onClick={resetAll}
          className="text-xs font-medium text-ohana-primary hover:text-ohana-primary-dark underline underline-offset-2"
          data-testid="sop-checklist-reset"
        >
          Reset checklist
        </button>
      </div>

      {SECTION_15_CHECKLIST.map((group) => (
        <div key={group.group}>
          <h3 className="font-serif text-lg text-ohana-primary">{group.group}</h3>
          <ul className="mt-2 space-y-1.5">
            {group.items.map((item, idx) => {
              const key = `${group.group}::${idx}`;
              const isChecked = !!checked[key];
              return (
                <li key={key}>
                  <label className="flex items-start gap-3 cursor-pointer rounded-md py-2 px-2 -mx-2 hover:bg-ohana-surface/60 active:bg-ohana-surface transition-colors">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(key)}
                      className="mt-1 h-5 w-5 rounded border-ohana-border text-ohana-primary focus:ring-ohana-primary/40 cursor-pointer accent-ohana-primary"
                      data-testid={`sop-checkbox-${group.group.toLowerCase().replace(/\s+/g, '-')}-${idx}`}
                    />
                    <span
                      className={
                        'text-[15px] leading-relaxed select-none ' +
                        (isChecked ? 'line-through text-ohana-text-muted' : 'text-ohana-text')
                      }
                    >
                      {item}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/* -------------------- PRINT VIEW -------------------- */

function PrintView() {
  // Auto-trigger browser print dialog once the content has rendered.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        window.print();
      } catch (err) {
        // Non-critical: localStorage / sessionStorage / clipboard can fail in private
        // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[sop] non-critical storage/clipboard op failed:', err);
        }
      }
    }, 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="bg-white text-black" data-testid="sop-print-view">
      <style>{`
        @page { size: letter; margin: 0.5in; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
        .print-section { break-inside: avoid; page-break-inside: avoid; }
        .print-h2 { break-after: avoid; page-break-after: avoid; }
      `}</style>

      <div className="no-print bg-yellow-50 border-b border-yellow-200 px-5 py-3 text-sm text-yellow-900 flex items-center justify-between gap-4 flex-wrap">
        <span>Print preview. Use your browser&apos;s print dialog (Cmd/Ctrl + P) if it did not open automatically.</span>
        <button
          type="button"
          onClick={() => window.print()}
          className="font-medium underline underline-offset-2 hover:no-underline"
          data-testid="sop-print-trigger"
        >
          Print now
        </button>
      </div>

      <div className="max-w-[7.5in] mx-auto px-6 py-8">
        {/* Document header */}
        <header className="border-b-2 border-black pb-4 mb-6">
          <p className="text-[10px] uppercase tracking-[0.25em] text-black/70">
            Internal. Field Operations Reference
          </p>
          <h1 className="font-serif text-2xl text-black mt-1 leading-tight">
            Ohana Property Services. Field Operations SOP
          </h1>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[12px] text-black">
            <p><span className="font-semibold">Legal entity:</span> {LEGAL_ENTITY}</p>
            <p className="sm:text-right"><span className="font-semibold">Last reviewed:</span> {LAST_REVIEWED}</p>
          </div>
          <p className="text-[11px] text-black/70 mt-2">
            For authorized Ohana personnel and approved subcontractors only. Do not distribute.
          </p>
        </header>

        {/* All sections, expanded */}
        <div className="space-y-6 text-black print-text">
          {SECTIONS.map((s) => (
            <section
              key={s.id}
              id={`print-${s.id}`}
              className="print-section"
            >
              <h2 className="print-h2 font-serif text-lg text-black border-b border-black/40 pb-1 mb-3">
                <span className="font-mono text-sm mr-2">§{s.number}</span>
                {s.title}
              </h2>
              <div className="text-black [&_a]:text-black [&_a]:underline [&_p]:text-black [&_li]:text-black [&_h3]:text-black [&_table]:border-black [&_th]:bg-black [&_th]:text-white [&_blockquote]:border-black [&_blockquote]:bg-white [&_blockquote]:text-black [&_strong]:text-black">
                <SectionContent id={s.id} />
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-10 pt-4 border-t border-black/40 text-[11px] text-black/70">
          <p>Ohana Property Services is a trade name of {LEGAL_ENTITY}, a registered Indiana limited liability company.</p>
          <p className="mt-1">Document last reviewed: {LAST_REVIEWED}</p>
        </footer>
      </div>
    </div>
  );
}

/* -------------------- ACCORDION SECTION -------------------- */

function AccordionSection({ section, isOpen, onToggle }) {
  const headerBg = section.critical
    ? 'bg-red-50 hover:bg-red-100 border-red-200'
    : 'bg-white hover:bg-ohana-surface/60 border-ohana-border';

  return (
    <section
      id={section.id}
      className="scroll-mt-20"
      data-testid={`sop-section-${section.number}`}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={`${section.id}-panel`}
        onClick={onToggle}
        className={`w-full text-left flex items-center gap-3 px-4 py-4 sm:py-4 border rounded-xl ${headerBg} transition-colors`}
        data-testid={`sop-toggle-${section.number}`}
      >
        <span
          className={
            'inline-grid place-items-center h-7 min-w-7 px-1.5 rounded-md text-xs font-semibold ' +
            (section.critical
              ? 'bg-red-700 text-white'
              : 'bg-ohana-primary text-white')
          }
        >
          {section.number}
        </span>
        <span className="flex-1 font-serif text-base sm:text-lg text-ohana-primary leading-tight">
          {section.title}
        </span>
        <svg
          className={`h-5 w-5 text-ohana-text-muted transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          id={`${section.id}-panel`}
          className="mt-2 mb-4 px-4 sm:px-5 py-5 bg-white border border-ohana-border rounded-xl"
        >
          <SectionContent id={section.id} />
        </div>
      )}
    </section>
  );
}

/* -------------------- MAIN PAGE -------------------- */

export default function Sop() {
  const [unlocked, setUnlocked] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();
  const isPrint = useMemo(
    () => new URLSearchParams(location.search).get('print') === '1',
    [location.search]
  );

  useEffect(() => {
    try {
      if (sessionStorage.getItem(STORAGE_KEY_AUTH) === '1') {
        setUnlocked(true);
      }
    } catch (err) {
      // Non-critical: localStorage / sessionStorage / clipboard can fail in private
      // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sop] non-critical storage/clipboard op failed:', err);
      }
    }
  }, []);

  const toggle = (id) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    const all = {};
    SECTIONS.forEach((s) => {
      all[s.id] = true;
    });
    setOpenSections(all);
  };

  const collapseAll = () => setOpenSections({});

  const handleJump = (e, id) => {
    e.preventDefault();
    setOpenSections((prev) => ({ ...prev, [id]: true }));
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY_AUTH);
    } catch (err) {
      // Non-critical: localStorage / sessionStorage / clipboard can fail in private
      // browsing or quota-exceeded scenarios. Surface in dev so it isn't invisible.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[sop] non-critical storage/clipboard op failed:', err);
      }
    }
    setUnlocked(false);
  };

  if (isPrint) {
    return (
      <>
        <SeoHead
          title="Field Operations SOP. Ohana Property Services"
          description="Internal field operations reference for authorized Ohana personnel."
          noindex
        />
        <PrintView />
      </>
    );
  }

  if (!unlocked) {
    return (
      <>
        <SeoHead
          title="Field Operations SOP. Ohana Property Services"
          description="Internal field operations reference for authorized Ohana personnel."
          noindex
        />
        <PinGate onUnlock={() => setUnlocked(true)} />
      </>
    );
  }

  return (
    <>
      <SeoHead
        title="Field Operations SOP. Ohana Property Services"
        description="Internal field operations reference for authorized Ohana personnel."
        noindex
      />

      <div className="min-h-screen flex flex-col bg-ohana-bg" data-testid="sop-page">
        {/* Branded header */}
        <header className="bg-ohana-primary text-white sticky top-0 z-30 shadow-sm">
          <div className="max-w-3xl mx-auto px-4 sm:px-5 py-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-white/10 grid place-items-center font-serif text-lg">
              O
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/70">
                Internal. Field Reference
              </p>
              <h1 className="text-[15px] sm:text-base font-serif leading-tight truncate">
                Ohana Property Services. Field Operations SOP
              </h1>
            </div>
            <a
              href="/internal/sop?print=1"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded-md px-2.5 py-1.5 transition-colors"
              data-testid="sop-print-button"
              title="Open printable view"
            >
              Print
            </a>
            <button
              type="button"
              onClick={handleLogout}
              className="text-xs font-medium text-white/80 hover:text-white border border-white/20 hover:border-white/40 rounded-md px-2.5 py-1.5 transition-colors"
              data-testid="sop-logout"
            >
              Lock
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 sm:px-5 py-6">
            {/* Last reviewed timestamp */}
            <div
              className="mb-4 flex items-center gap-2 text-xs text-ohana-text-muted"
              data-testid="sop-last-reviewed"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .2.08.39.22.53l3 3a.75.75 0 101.06-1.06L10.75 9.69V5z" clipRule="evenodd" />
              </svg>
              <span>Last reviewed: <span className="font-medium text-ohana-text">{LAST_REVIEWED}</span></span>
            </div>

            {/* Quick jump links */}
            <nav
              aria-label="Section quick jump"
              className="bg-white border border-ohana-border rounded-xl p-4"
              data-testid="sop-quick-jump"
            >
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs uppercase tracking-wider text-ohana-text-muted font-medium">
                  Quick Jump
                </p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={expandAll}
                    className="text-xs font-medium text-ohana-primary hover:text-ohana-primary-dark underline underline-offset-2"
                    data-testid="sop-expand-all"
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="text-xs font-medium text-ohana-primary hover:text-ohana-primary-dark underline underline-offset-2"
                    data-testid="sop-collapse-all"
                  >
                    Collapse all
                  </button>
                </div>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-4">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => handleJump(e, s.id)}
                      className="flex items-baseline gap-2 text-[14px] py-1 text-ohana-text hover:text-ohana-primary"
                      data-testid={`sop-jump-${s.number}`}
                    >
                      <span className="text-xs font-mono text-ohana-text-muted shrink-0 w-7">
                        §{s.number}
                      </span>
                      <span className="leading-snug">{s.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Sections */}
            <div className="mt-5 space-y-2">
              {SECTIONS.map((s) => (
                <AccordionSection
                  key={s.id}
                  section={s}
                  isOpen={!!openSections[s.id]}
                  onToggle={() => toggle(s.id)}
                />
              ))}
            </div>

            <p className="mt-8 text-center text-xs text-ohana-text-muted">
              Internal document. Authorized Ohana personnel and approved subcontractors only.
            </p>
          </div>
        </main>
      </div>
    </>
  );
}
